import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

// components
import StepperNavMobile from '@molecules/StepperNavMobile';
import AccountInformationForm from '@organisms/AccountInformationForm';
import { BodyContainerStyled } from '@styled';
import TrackPageView from '@tracking/TrackPageView';
import ErrorHandler from '../../../_molecules/ErrorHandler';
import FooterNavigation from '../../../_molecules/FooterNavigation';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';

// Functionality
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import { useScreenMinMd, useScreenSmDown } from '@hooks/useResponsive';
import useViewer from '@hooks/useViewer';
import {
    getDoesCartHaveEventRegistrations,
    getDoesCartHaveMemberships,
    membershipYears,
    updateMembershipInCart,
} from '@general/helpers/cart.helpers';
import { getMemberInfoBreadcrumbLink } from '@general/helpers/member.helpers';
import { useQueryClient } from 'react-query';

const BreadCrumbContainerStyled = styled.div`
    background-color: #f9f9f9;
`;

const CartAccountInfoPage = () => {
    const queryClient = useQueryClient();
    const [hasMembershipsInCart, setHasMembershipsInCart] = useState();
    const [hasEventRegistrationsInCart, setHasEventRegistrationsInCart] = useState();
    const history = useHistory();
    const formRef = useRef();
    const match = useRouteMatch();
    const eventCode = match.params?.code;
    const memberId = parseInt(match.params?.memberId);
    const { viewer } = useViewer();
    const { cart, refetch } = useCartFromAPI({ viewerId: viewer?.recordId });
    const isScreenMinMd = useScreenMinMd();
    const isScreenSmDown = useScreenSmDown();

    // Redirect if the cart doesn't contain an item for the member/event
    useEffect(() => {
        if (!cart) {
            return;
        }

        if (!cart.items.find(item => item.member_id === memberId)) {
            history.push(`/cart/view`);
        }
    }, [cart, eventCode, history, memberId]);

    useEffect(() => {
        if (!memberId || !cart) {
            return;
        }

        setHasMembershipsInCart(getDoesCartHaveMemberships({ cart, memberId }));
        setHasEventRegistrationsInCart(getDoesCartHaveEventRegistrations({ cart, memberId }));
    }, [memberId, cart]);

    if (!cart) {
        return null;
    }

    return (
        <ErrorHandler>
            <TrackPageView>
                {isScreenMinMd && (
                    <BreadCrumbContainerStyled>
                        <div className="container">
                            <div className="py-1rem">
                                <SectionBreadcrumbs className="mt-2">
                                    <SectionBreadcrumb isActive>Account Info</SectionBreadcrumb>
                                    <SectionBreadcrumb route="/cart/view">Cart Details</SectionBreadcrumb>
                                    <SectionBreadcrumb route="/cart/payment">Payment Info</SectionBreadcrumb>
                                </SectionBreadcrumbs>
                            </div>
                        </div>
                    </BreadCrumbContainerStyled>
                )}

                {isScreenSmDown && (
                    <StepperNavMobile
                        activeIndex={0}
                        steps={[
                            { label: 'Account Info', href: getMemberInfoBreadcrumbLink(cart, viewer?.recordId) },
                            { label: 'Cart Details', href: '/cart/view' },
                            { label: 'Payment Info', href: '/cart/payment' },
                        ]}
                    />
                )}

                <BodyContainerStyled className="container">
                    <div className="gap-8 grid-cols-8 md:grid">
                        <div className="col-span-5">
                            <AccountInformationForm
                                disableCompanySelection={
                                    (!hasMembershipsInCart && !viewer?.prospect) || memberId !== viewer?.recordId
                                }
                                formRef={formRef}
                                memberId={memberId}
                                onMemberSaved={async member => {
                                    await updateMembershipInCart({
                                        cart,
                                        memberId: member.id,
                                        viewerId: viewer.recordId,
                                        years: membershipYears.THREE,
                                    });

                                    await refetch();
                                    // invalidate the memberdues (and any other query) that may have change
                                    // due to member data changing
                                    queryClient.invalidateQueries({
                                        queryKey: [`member-dues-eligibility-${member.id}`],
                                    });

                                    history.push('/cart/view');
                                }}
                                viewerId={viewer?.recordId}
                            />
                        </div>
                    </div>
                </BodyContainerStyled>

                <FooterNavigation
                    nextLabel="Save & Continue"
                    onNextClick={() => {
                        if (formRef.current) {
                            formRef.current.handleSubmit();
                        }
                    }}
                />
            </TrackPageView>
        </ErrorHandler>
    );
};

export default CartAccountInfoPage;
